import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type { SettingGroupDefinition } from "../../settings-catalog/types";
import {
  FulfillmentFlatTeamSettingGroups as originalGroups,
  type FulfillmentFlatTeamSettingGroupId as OriginalGroupId,
} from "./groups";

const additionalGroups = [
  {
    id: "fulfillment.international_shipping",
    title: "Default customs information",
    description: "Set customs defaults for international shipments.",
    searchLabels: ["International shipping", "International shipments"],
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.customs",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason: "Changes customs declarations used for international shipments.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=international",
    },
  },
  {
    id: "fulfillment.tax_identifiers",
    title: "Tax identifiers",
    description:
      "Manage tax identifiers used on outbound customs declarations.",
    searchLabels: ["International shipping tax identifiers"],
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: { kind: "provider", product: "fulfillment" },
    risk: {
      level: "medium",
      reason: "Changes tax identification supplied on customs declarations.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=international",
    },
  },
  {
    id: "fulfillment.print_profiles",
    title: "Print profiles",
    searchLabels: [
      "Profile",
      "Profile name",
      "Orientation",
      "Number of copies",
      "Print packing slip on the same printer as the label",
      'Print packing slip on the 8.5" x 11" printer',
    ],
    description:
      "Create output profiles and assign the right printers and document behavior to each workstation.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason: "Changes the documents and printers used during fulfillment.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=printing",
    },
  },
  {
    id: "fulfillment.printers",
    title: "Printers & workstations",
    searchLabels: [
      "Redo Connect computer",
      "Name",
      "Print profile",
      "Default preset",
      "Printer",
      "Paper size",
      "Printer bin",
      "Rotate",
      "DPI",
      "Fit to page",
      "Duplex",
    ],
    description:
      "Connect local workstations to Redo and verify their printing setup.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason: "Changes the documents and printers used during fulfillment.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=printing",
    },
  },
  {
    id: "fulfillment.print_actions",
    title: "Fulfillment actions",
    description:
      "Choose the default fulfillment action and one-click printing preferences.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason: "Changes the documents and printers used during fulfillment.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=printing",
    },
  },
  {
    id: "fulfillment.customer_notifications",
    title: "Customer notifications",
    description:
      "Email customers about address corrections and pickup readiness.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
  },
  {
    id: "fulfillment.safeguards",
    title: "Fulfillment safeguards",
    description:
      "Prevent shipping beyond available inventory and require approval for selected actions.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
  },
  {
    id: "fulfillment.package_photos",
    title: "Package verification photos",
    description:
      "Capture photos of packed packages for dispute evidence and returns reference.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
  },
  {
    id: "fulfillment.display",
    title: "Display & units",
    description: "Display & units",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=display",
    },
  },
  {
    id: "fulfillment.bundles",
    title: "Bundles",
    description: "Bundles",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
  },
  {
    id: "fulfillment.return_label_defaults",
    title: "Return label defaults",
    description: "For return labels created during outbound fulfillment.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
  },
  {
    id: "fulfillment.custom_field_mappings",
    title: "Custom field mappings",
    description:
      "Choose existing order, product, and variant fields to use in fulfillment table columns and CSV exports, and set their display names in Redo.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=display",
    },
  },
  {
    id: "fulfillment.merge_outcomes",
    title: "Merged fulfillment outcomes",
    description:
      "Control the service, refunds, tags, and notes applied after fulfillments are merged.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=merging",
    },
  },
  {
    id: "fulfillment.merge_exceptions",
    title: "Auto-merge exceptions",
    description:
      "Do not automatically merge shipments when any of these conditions apply. Orders matching your auto-split conditions also won't be automatically re-merged.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=merging",
    },
  },
  {
    id: "fulfillment.split_outcomes",
    title: "Split shipment outcomes",
    description:
      "Choose how matching items are split and which tags to add afterward.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=splitting",
    },
  },
  {
    id: "fulfillment.split_sku_mappings",
    title: "Product SKU mappings",
    description:
      "Keep companion products with a trigger SKU when that SKU is split into its own shipment.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=splitting",
    },
  },
  {
    id: "fulfillment.split_exceptions",
    title: "Split exceptions",
    description:
      "Prevent splitting orders that match any of the selected criteria.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes can affect fulfillment operations and customer communications.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=splitting",
    },
  },
  {
    id: "fulfillment.merge_suggestions",
    title: "Merge suggestions",
    description:
      "Suggest merging fulfillments that meet every selected requirement.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.suggestMerge",
      product: "fulfillment",
    },
    risk: {
      level: "low",
      reason: "Changes suggestions without automatically merging shipments.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=merging",
    },
  },
  {
    id: "fulfillment.auto_merge",
    title: "Auto-merge",
    description:
      "Automatically merge fulfillments that meet every selected requirement and none of the configured exceptions.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.autoMerge",
      product: "fulfillment",
    },
    risk: {
      level: "high",
      reason: "Changes which shipments are combined automatically.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=merging",
    },
  },
  {
    id: "fulfillment.remember_shipment_choices",
    title: "Remember shipment choices",
    description:
      "Reuse your package and weight choices for future shipments with matching contents.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.learnedPresets",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Reused package and weight choices can affect shipping rates and labels.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
    synonyms: ["automated learning"],
  },
  {
    id: "fulfillment.batch_suggestions",
    title: "Batch suggestions",
    description:
      "Group similar shipments for processing together. Each shipment stays separate.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.batchSuggestions",
      product: "fulfillment",
    },
    risk: {
      level: "low",
      reason:
        "Changes which shipments are suggested for processing together, without merging them.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
    synonyms: ["bulk shipment suggestions", "bulk shipment matching"],
  },
  {
    id: "fulfillment.auto_split",
    title: "Auto-split",
    description:
      "How Redo separates matching items into their own fulfillment groups.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.outboundLabels.autoSplit",
      product: "fulfillment",
    },
    risk: {
      level: "medium",
      reason:
        "Changes affect how items are grouped into shipments during fulfillment.",
    },
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=splitting",
    },
    synonyms: ["automatic order splitting", "split fulfillment groups"],
  },
] as const satisfies readonly SettingGroupDefinition[];

export type FulfillmentFlatTeamSettingGroupId =
  | OriginalGroupId
  | (typeof additionalGroups)[number]["id"];

const groupOverrides: Record<
  string,
  Partial<Omit<SettingGroupDefinition, "id">>
> = {
  "fulfillment.ship_from_locations": {
    title: "Fulfillment locations",
    searchLabels: ["Return addresses"],
    description:
      "Manage Ship from locations and Return addresses used to originate and receive shipments.",
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=locations",
    },
  },
  "fulfillment.address": {
    title: "Address validation",
    description:
      "Clean up shipping addresses, hold orders that need correction, and apply address holds and correction emails outside the US. Configure correction emails under Customer notifications.",
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=order-preparation",
    },
  },
  "fulfillment.scan_to_verify": { fullPageTarget: { type: "scanToVerify" } },
  "fulfillment.printing": {
    title: "Documents",
    fullPageTarget: {
      type: "settings",
      path: "order-management?section=printing",
    },
  },
};

export const FulfillmentFlatTeamSettingGroups = [
  ...additionalGroups.slice(0, -1),
  ...originalGroups.map((group) => ({ ...group, ...groupOverrides[group.id] })),
  ...additionalGroups.slice(-1),
] satisfies readonly SettingGroupDefinition<FulfillmentFlatTeamSettingGroupId>[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const FulfillmentFlatTeamSettingGroupById = Object.fromEntries(
  FulfillmentFlatTeamSettingGroups.map((group) => [group.id, group]),
) as Record<
  FulfillmentFlatTeamSettingGroupId,
  SettingGroupDefinition<FulfillmentFlatTeamSettingGroupId>
>;
